/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { orderBy, isEqual } from 'lodash';
import {
  updateOrganizationLevel,
  deleteOrganizationLevel,
  restoreOrganizationLevel,
  createOrganizationLevel,
  changeDefaultOrganizationLevel
} from '../../actions/organizationActions';
import Default from '../../constants/defaults';
import LevelDropDown from './levelDropDown';
import { allows } from '../../utils/authorization';

const Button = ({ title, disabled, onClick, iconName }) => (
  <button
    type="button"
    className={`btn btn-link ${title === 'Delete' ? 'red' : ''}`}
    title={title}
    disabled={disabled}
    onClick={onClick}
  >
    <i className={`glyphicon ${iconName}`} />
  </button>
);

class OrganizationTypesTab extends React.Component {
  initialOrganizationLevel = {
    name: '',
    level: 1
  };

  constructor(props) {
    super(props);
    this.state = {
      editing: -1,
      organizationLevels: [],
      organizationLevel: this.initialOrganizationLevel,
      showCharacterCount: false
    };
  }

  componentDidMount() {
    const { organization } = this.props;
    if (organization.id === -1) return;
    const { organization_levels: organizationLevels } = organization;
    this.setState({ organizationLevels });
  }

  componentDidUpdate(prevProps) {
    if (
      !isEqual(
        prevProps.organization.organization_levels,
        this.props.organization.organization_levels
      )
    ) {
      this.setState({
        organizationLevels: this.props.organization.organization_levels
      });
    }
  }

  handleFieldChange = (e) => {
    if (e.target.value.length > 25) return;
    const { value } = e.target;

    this.setState((prevState) => ({
      ...prevState,
      organizationLevel: {
        ...prevState.organizationLevel,
        name: value
      }
    }));
  };

  handleDropDownChange = (value) => {
    this.setState((prevState) => ({
      ...prevState,
      organizationLevel: {
        ...prevState.organizationLevel,
        level: value
      }
    }));
  };

  changeOrganizationLevel = (event, orgLevelsId) => {
    const { organizationLevels } = this.state;
    const newOrganizationLevels = organizationLevels.map((orgLevel) => {
      const newOrgLevel = { ...orgLevel };
      if (orgLevel.id === orgLevelsId) {
        newOrgLevel[event.target.name] = event.target.value;
      }
      return newOrgLevel;
    });
    this.setState({ organizationLevels: newOrganizationLevels });
  };

  updateOrganizationLevel = (organizationLevelId) => {
    const { organization } = this.props;
    const { organizationLevels } = this.state;
    const organizationLevel = organizationLevels.find(
      (orgLevel) => orgLevel.id === organizationLevelId
    );
    const prevOrganizationLevel = organization.organization_levels.find(
      (org) => org.id === organizationLevelId
    );
    if (!organizationLevel.name) return;
    if (prevOrganizationLevel.name !== organizationLevel.name)
      this.props.updateOrganizationLevel(organization.id, organizationLevel);
    this.setState({ editing: -1 });
  };

  deleteOrganizationLevel = (organizationLevel) => {
    const { organization } = this.props;
    const question = organizationLevel.deleted_at
      ? window.confirm('Do you want to delete it permanetly?')
      : window.confirm('Are you sure?');
    if (question) {
      this.props.deleteOrganizationLevel(organization, organizationLevel);
    }
  };

  restoreOrganizationLevel = (organizationLevel) => {
    const { organization } = this.props;
    this.props.restoreOrganizationLevel(organization, organizationLevel);
  };

  createOrganizationLevel = (e) => {
    e.preventDefault();
    const { organization } = this.props;
    const { organizationLevel } = this.state;
    this.props.createOrganizationLevel(organization.id, organizationLevel);
    this.setState({ organizationLevel: this.initialOrganizationLevel });
  };

  isDefectLevel = (levelName) => Default.resultTypes.defect === levelName;

  shouldRenderEditBtn = (orgLevel) => {
    const { editing } = this.state;
    if (editing !== orgLevel.id && !orgLevel.deleted_at) return true;
    return false;
  };

  handleDefaultCheckboxChange = (checkedLevel) => {
    const { organization, changeDefaultOrganizationLevel } = this.props;
    const { organizationLevels } = this.state;

    const atleastOneDefault = organizationLevels.some(
      (orgLevel) =>
        orgLevel.default_level === true && checkedLevel.level === orgLevel.level
    );

    organizationLevels.forEach((orgLevel) => {
      if (orgLevel.id === checkedLevel.id && orgLevel.default_level === true)
        return;

      if (
        orgLevel.level === checkedLevel.level &&
        orgLevel.default_level === true &&
        orgLevel.id !== checkedLevel.id
      ) {
        changeDefaultOrganizationLevel(
          orgLevel.id,
          checkedLevel.id,
          organization.id
        );
        return;
      }

      if (
        orgLevel.id === checkedLevel.id &&
        !orgLevel.default_level &&
        !atleastOneDefault
      ) {
        changeDefaultOrganizationLevel(null, checkedLevel.id, organization.id);
      }
    });
  };

  handleInputFocus = () => {
    this.setState({ showCharacterCount: true });
  };

  handleInputBlur = () => {
    this.setState({ showCharacterCount: false });
  };

  counterStyling = () => {
    const {
      showCharacterCount,
      organizationLevel: { name }
    } = this.state;
    return {
      display: showCharacterCount ? 'block' : 'none',
      color: name.length === 25 ? '#b70606' : 'gray'
    };
  };

  render() {
    const { editing, organizationLevels = [], organizationLevel } = this.state;

    const { organization, showTrash } = this.props;
    const levels = [1, 2, 3, 4];
    const allowEdit =
      organization && allows('administer', organization, 'organization');
    /* const levels = orderBy(organizationLevels, 'level').reduce((all, orgLevel) => {
      if (all.indexOf(orgLevel.level) === -1) {
        all.push(orgLevel.level);
      }
      return all
    }, []); */
    // User can add one level above the higher one
    // if (levels.length > 0) levels.push(levels[levels.length - 1] + 1);

    return (
      <div
        role="tabpanel"
        className="tab-pane active team-details-tab organization-names-tab"
        id="details"
      >
        {allowEdit ? (
          <form
            className="form-inline add-story-input row organization"
            onSubmit={this.createOrganizationLevel}
          >
            <div>
              <label>Name</label>
              <div className="org-name-input-container">
                <input
                  type="text"
                  name="name"
                  value={organizationLevel.name}
                  onChange={this.handleFieldChange}
                  onFocus={this.handleInputFocus}
                  onBlur={this.handleInputBlur}
                  autoComplete="off"
                />
                <div
                  className="org-input-counter"
                  style={{ ...this.counterStyling() }}
                >
                  {25 - this.state.organizationLevel.name.length}
                </div>
              </div>
            </div>
            <div>
              <label>Level</label>
              <LevelDropDown
                handleSelectChange={this.handleDropDownChange}
                selectedValue={this.state.organizationLevel.level}
              />
            </div>
            <div>
              <label>&nbsp;</label>
              <button type="submit" className="btn">
                Add New
              </button>
            </div>
          </form>
        ) : null}
        <div className="table-wrapper">
          <div className="table-scroll">
            <table className="table levels-table">
              <thead>
                <tr>
                  <th>
                    <span className="table-header-text first-text">Name</span>
                  </th>
                  <th style={tableCentering}>
                    <span className="table-header-text">Level</span>{' '}
                  </th>
                  <th style={tableCentering}>
                    <span className="table-header-text">Actions</span>
                  </th>
                  <th style={tableCentering}>
                    <span className="table-header-text">Default</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {orderBy(organizationLevels, ['level', 'id']).map(
                  (orgLevel) => {
                    if (orgLevel.deleted_at && !showTrash) return null;
                    let defaultLevel;
                    if (orgLevel.default_level === null) defaultLevel = false;
                    else defaultLevel = orgLevel.default_level;
                    return (
                      <tr
                        key={orgLevel.id}
                        className={orgLevel.deleted_at ? 'deleted' : ''}
                      >
                        <td>
                          {editing === orgLevel.id && (
                            <input
                              autoFocus
                              type="text"
                              className="form-control"
                              value={orgLevel.name}
                              name="name"
                              onChange={(e) =>
                                this.changeOrganizationLevel(e, orgLevel.id)
                              }
                              onBlur={() =>
                                this.updateOrganizationLevel(orgLevel.id)
                              }
                            />
                          )}
                          {editing !== orgLevel.id && orgLevel.name}
                        </td>
                        <td style={tableCentering}>{orgLevel.level}</td>
                        <td style={tableCentering}>
                          {this.shouldRenderEditBtn(orgLevel) &&
                            orgLevel.name !== 'Defect' && (
                              <Button
                                title="Edit"
                                disabled={!allowEdit}
                                onClick={() =>
                                  this.setState({ editing: orgLevel.id })
                                }
                                iconName="glyphicon-pencil"
                              />
                            )}
                          {!orgLevel.default_level && (
                            <Button
                              title="Delete"
                              disabled={!allowEdit}
                              onClick={() =>
                                this.deleteOrganizationLevel(orgLevel)
                              }
                              iconName="glyphicon-remove"
                            />
                          )}
                          {orgLevel.deleted_at && (
                            <Button
                              title="Restore"
                              disabled={!allowEdit}
                              onClick={() =>
                                this.restoreOrganizationLevel(orgLevel)
                              }
                              iconName="glyphicon-refresh"
                            />
                          )}
                        </td>
                        <td style={tableCentering}>
                          <input
                            type="checkbox"
                            checked={defaultLevel}
                            disabled={!allowEdit}
                            onChange={() =>
                              this.handleDefaultCheckboxChange(orgLevel)
                            }
                          />
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
}

const tableCentering = {
  textAlign: 'center'
};

const mapStateToProps = (state) => ({
  currentUser: state.userReducer.user
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateOrganizationLevel,
      deleteOrganizationLevel,
      restoreOrganizationLevel,
      createOrganizationLevel,
      changeDefaultOrganizationLevel
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrganizationTypesTab);
